/**
 * Symbol Formatter — port of trading-bot-app's lib/symbolFormatter.ts.
 *
 * Provides 6 option-display formats + parse/reformat helpers. Used by
 * <SymbolDisplay> everywhere options are shown. The user picks their
 * preferred format in Settings → Appearance.
 *
 * Compass divergence from the bot: only the font side is trimmed.
 * settingsStore.ts already curates 3 SymbolFont choices (mono / jetbrains
 * / inter); we don't import font types from this file. The full bot
 * formatter's 8-font menu was perceived as too many near-duplicates.
 */

export type SymbolFormat =
  | 'osi'          // RIVN  260102C00019000 (OSI/OCC standard)
  | 'compact'      // RIVN 02JAN26 19C
  | 'dashed_mdy'   // RIVN JAN-02-26 19C
  | 'dashed_dmy'   // RIVN 02-JAN-26 19C
  | 'readable'     // RIVN Jan 02 '26 19 Call
  | 'numeric'      // RIVN 01/02/26 19C

export interface SymbolFormatOption {
  id: SymbolFormat
  label: string
  example: string
  description: string
}

export const SYMBOL_FORMAT_OPTIONS: SymbolFormatOption[] = [
  {
    id: 'compact',
    label: 'Compact',
    example: 'RIVN 02JAN26 19C',
    description: 'Traditional compact format (DDMMMYY) with joined strike/type',
  },
  {
    id: 'dashed_mdy',
    label: 'Dashed (MMM-DD-YY)',
    example: 'RIVN JAN-02-26 19C',
    description: 'Month-day-year with dashes',
  },
  {
    id: 'dashed_dmy',
    label: 'Dashed (DD-MMM-YY)',
    example: 'RIVN 02-JAN-26 19C',
    description: 'Day-month-year with dashes',
  },
  {
    id: 'readable',
    label: 'Readable',
    example: "RIVN Jan 02 '26 19 Call",
    description: 'Human-readable with full option type and spacing',
  },
  {
    id: 'numeric',
    label: 'Numeric Date',
    example: 'RIVN 01/02/26 19C',
    description: 'Numeric date format (MM/DD/YY)',
  },
  {
    id: 'osi',
    label: 'OSI Standard',
    example: 'RIVN  260102C00019000',
    description: 'Options Symbology Initiative standard (OCC format)',
  },
]

export const DEFAULT_SYMBOL_FORMAT: SymbolFormat = 'numeric'

interface SymbolParts {
  underlying: string
  expiry: string | number | Date | null | undefined
  strike: number | string | null | undefined
  right: string | null | undefined // 'C', 'P', 'CALL', 'PUT', etc.
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_UPPER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/**
 * Parse an expiry string/number into a Date. Handles YYYY-MM-DD,
 * YYYYMMDD, YYYYMM, DDMMMYY, numbers, etc. Returns Date in *local* time
 * to avoid UTC off-by-one-day rendering.
 */
function parseExpiry(expiry: string | number | Date | null | undefined): Date | null {
  if (!expiry && expiry !== 0) return null
  if (expiry instanceof Date) return expiry

  const expiryStr = String(expiry).trim()

  if (/^\d{4}-\d{2}-\d{2}/.test(expiryStr)) {
    const [year, month, day] = expiryStr.substring(0, 10).split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const yyyymmddMatch = expiryStr.match(/^(\d{4})(\d{2})(\d{2})/)
  if (yyyymmddMatch) {
    const year = parseInt(yyyymmddMatch[1])
    const month = parseInt(yyyymmddMatch[2]) - 1
    const day = parseInt(yyyymmddMatch[3])
    return new Date(year, month, day)
  }

  if (/^\d{6}$/.test(expiryStr)) {
    const year = parseInt(expiryStr.substring(0, 4))
    const month = parseInt(expiryStr.substring(4, 6)) - 1
    return new Date(year, month, 20) // monthly expiry approximation
  }

  const compactMatch = expiryStr.match(/^(\d{1,2})([A-Z]{3})(\d{2})$/i)
  if (compactMatch) {
    const day = parseInt(compactMatch[1])
    const monthStr = compactMatch[2].toUpperCase()
    const year = 2000 + parseInt(compactMatch[3])
    const month = MONTHS_UPPER.indexOf(monthStr)
    if (month !== -1) return new Date(year, month, day)
  }

  const parsed = new Date(expiryStr)
  return isNaN(parsed.getTime()) ? null : parsed
}

function normalizeRight(right: string | null | undefined): 'C' | 'P' | null {
  if (!right) return null
  const upper = right.toUpperCase().trim()
  if (upper === 'C' || upper === 'CALL') return 'C'
  if (upper === 'P' || upper === 'PUT') return 'P'
  return null
}

function formatStrike(strike: number | string | null | undefined): string {
  if (strike === null || strike === undefined) return ''
  const num = typeof strike === 'string' ? parseFloat(strike) : strike
  if (isNaN(num)) return String(strike)
  return num % 1 === 0 ? num.toFixed(0) : num.toString()
}

export function formatSymbol(parts: SymbolParts, format: SymbolFormat = DEFAULT_SYMBOL_FORMAT): string {
  const { underlying } = parts
  const date = parseExpiry(parts.expiry)
  const strike = formatStrike(parts.strike)
  const right = normalizeRight(parts.right)

  if (!date || !strike || !right) return underlying || ''

  const day = date.getDate()
  const month = date.getMonth()
  const year = date.getFullYear()
  const yearShort = year % 100

  const dayPadded = day.toString().padStart(2, '0')
  const monthPadded = (month + 1).toString().padStart(2, '0')
  const yearPaddedShort = yearShort.toString().padStart(2, '0')

  switch (format) {
    case 'osi': {
      const osiUnderlying = underlying.padEnd(6, ' ')
      const osiDate = `${yearPaddedShort}${monthPadded}${dayPadded}`
      const strikeNum = typeof parts.strike === 'string' ? parseFloat(parts.strike) : parts.strike
      const osiStrike = Math.round((strikeNum || 0) * 1000).toString().padStart(8, '0')
      return `${osiUnderlying}${osiDate}${right}${osiStrike}`
    }
    case 'compact':
      return `${underlying} ${dayPadded}${MONTHS_UPPER[month]}${yearPaddedShort} ${strike}${right}`
    case 'dashed_mdy':
      return `${underlying} ${MONTHS_UPPER[month]}-${dayPadded}-${yearPaddedShort} ${strike}${right}`
    case 'dashed_dmy':
      return `${underlying} ${dayPadded}-${MONTHS_UPPER[month]}-${yearPaddedShort} ${strike}${right}`
    case 'readable': {
      const fullRight = right === 'C' ? 'Call' : 'Put'
      return `${underlying} ${MONTHS_SHORT[month]} ${dayPadded} '${yearPaddedShort} ${strike} ${fullRight}`
    }
    case 'numeric':
    default:
      return `${underlying} ${monthPadded}/${dayPadded}/${yearPaddedShort} ${strike}${right}`
  }
}

/**
 * Parse a raw symbol string (OSI/OCC or space-separated) into parts.
 * Returns null if the format isn't recognized.
 */
export function parseSymbolString(symbol: string): SymbolParts | null {
  if (!symbol) return null

  // OSI/OCC: RIVN  260102C00019000
  const occMatch = symbol.match(/^([A-Z]+)\s*(\d{6})([CP])(\d{8})$/i)
  if (occMatch) {
    const underlying = occMatch[1]
    const dateStr = occMatch[2]
    const right = occMatch[3].toUpperCase()
    const strikeStr = occMatch[4]

    const year = 2000 + parseInt(dateStr.substring(0, 2))
    const month = parseInt(dateStr.substring(2, 4)) - 1
    const day = parseInt(dateStr.substring(4, 6))
    const strike = parseInt(strikeStr) / 1000

    return { underlying, expiry: new Date(year, month, day), strike, right }
  }

  // Space-separated: RIVN 02JAN26 19 C
  const parts = symbol.trim().split(/\s+/)
  if (parts.length >= 4) {
    const underlying = parts[0]
    const dateStr = parts[1]
    const strike = parseFloat(parts[2])
    const right = parts[3]
    const date = parseExpiry(dateStr)
    if (date && !isNaN(strike)) {
      return { underlying, expiry: date, strike, right }
    }
  }
  return null
}

/**
 * Convert a raw symbol string to the user's preferred format.
 * Falls back to the original string if the format isn't recognized.
 */
export function reformatSymbol(symbol: string, format: SymbolFormat = DEFAULT_SYMBOL_FORMAT): string {
  const parts = parseSymbolString(symbol)
  if (!parts) return symbol
  return formatSymbol(parts, format)
}
